import {useEffect, useRef, useState} from "react";
import log from "@/lib/logger";
import {useAdminTranslation, useCommonTranslation} from "@/i18n/hooks";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Button} from "@/components/tailwind/button.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {CheckCircle, GripVertical, Pencil, Plus, Trash, XCircle, AlertCircle} from "lucide-react";
import {v4 as uuid} from "uuid";
import {ResignationReason} from "@/models/resignation-reason.ts";
import {Pagination} from "@/components/pagination.tsx";
import LoadingSpinner from "@/components/loader-spinner.tsx";
import {
    createResignationReason,
    deleteResignationReason,
    fetchAllResignationReasons,
    updateResignationReasonDetails,
    updateResignationReasonOrder
} from "@/services/resignation-reason-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import {useToast} from "@/hooks/use-toast.ts";

type SortableItemProps = {
    reason: ResignationReason;
    editingId: string | null;
    handleStartEditing: (id: string) => void;
    handleSaveEditing: (id: string, newName: string) => void;
    handleCancelEditing: () => void;
    handleDelete: (id: string) => void;
    t: any;
    isUpdating: boolean;
    disableDelete: boolean;
};

function SortableItem({
                          reason,
                          editingId,
                          handleStartEditing,
                          handleSaveEditing,
                          handleCancelEditing,
                          handleDelete,
                          t,
                          isUpdating,
                          disableDelete,
                      }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({id: reason.id});

    const [editName, setEditName] = useState(reason.name);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 bg-white dark:bg-zinc-900 shadow-sm flex items-center justify-between"
            data-testid={`resignation-reason-item-${reason.id}`}
        >
            {editingId === reason.id ? (
                <div className="flex items-center w-full gap-4">
                    <Input
                        className="flex-grow"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        id={`edit-reason-${reason.id}`}
                        placeholder={t("adminSettings.resignationReasons.namePlaceholder")}
                        data-testid={`edit-reason-name-${reason.id}`}
                    />
                    <Button
                        plain
                        onClick={() => handleSaveEditing(reason.id, editName)}
                        data-testid={`save-edit-reason-${reason.id}`}
                    >
                        <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400"/>
                    </Button>

                    <Button
                        plain
                        onClick={handleCancelEditing}
                        data-testid={`cancel-edit-reason-${reason.id}`}
                    >
                        <XCircle className="h-5 w-5 text-red-500 dark:text-red-400"/>
                    </Button>
                </div>
            ) : (
                <>
                    <div className="flex items-center">
                        <div
                            className="flex items-center justify-center mr-3 cursor-move text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
                            {...attributes}
                            {...listeners}
                        >
                            <GripVertical className="h-5 w-5"/>
                        </div>
                        <span className="mr-2 text-zinc-500 dark:text-zinc-400">#{reason.sortOrder}</span>
                        <Text className="text-zinc-900 dark:text-zinc-100" data-testid={`reason-name-${reason.id}`}>{reason.name}</Text>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            plain
                            onClick={() => handleStartEditing(reason.id)}
                            title={t("adminSettings.resignationReasons.edit")}
                            disabled={isUpdating}
                            data-testid={`edit-reason-${reason.id}`}
                        >
                            <Pencil
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!isUpdating ? 'hover:text-blue-500 dark:hover:text-blue-400' : ''}`}/>
                        </Button>
                        <Button
                            plain
                            onClick={() => handleDelete(reason.id)}
                            title={t("adminSettings.resignationReasons.delete")}
                            disabled={disableDelete}
                            data-testid={`delete-reason-${reason.id}`}
                        >
                            <Trash
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!disableDelete ? 'hover:text-red-500 dark:hover:text-red-400' : ''}`}/>
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

interface SectionResignationReasonProps {
    isMissing?: boolean;
}

function SectionResignationReason({ isMissing = false }: SectionResignationReasonProps) {
    const {t} = useAdminTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth()
    const {toast} = useToast()

    const [reasons, setReasons] = useState<ResignationReason[]>([]);
    const [prevReasons, setPrevReasons] = useState<ResignationReason[]>([]);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [hasOrderChanged, setHasOrderChanged] = useState<boolean>(false);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [newReason, setNewReason] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isAdding, setIsAdding] = useState(false);
    const [addingIssue, setAddingIssue] = useState<string | null>(null);
    const [orderUpdateIssue, setOrderUpdateIssue] = useState<string | null>(null);
    const [editReasonIssue, setEditReasonIssue] = useState<string | null>(null);

    const isFirstRender = useRef(true);

    const itemsPerPage = 5;
    const pageCount = Math.ceil(reasons.filter(r => !r.isDeleted).length / itemsPerPage);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function showToast(title: string, description: string, variant: "default" | "destructive" = "default") {
        return (
            toast({
                title: title,
                description: description,
                variant: variant
            })
        )
    }

    async function fetchResignationReasons() {
        setIsUpdating(true);
        try {
            const fetched = await fetchAllResignationReasons(token)

            if (fetched) {
                setReasons(
                    fetched.sort((a, b) => a.sortOrder - b.sortOrder)
                );
                setPrevReasons([])
            }
        } catch (error) {
            log.error("Error fetching resignation reasons. Error: ", error);
        }

        setIsUpdating(false)
    }

    useEffect(() => {
        fetchResignationReasons()
    }, []);

    useEffect(() => {

        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (isUpdating || !hasOrderChanged)
            return

        const timer = setTimeout(() => {
            updateReasonOrderInBackend(reasons)
        }, 300)

        return () => clearTimeout(timer)
    }, [reasons, isUpdating, hasOrderChanged])

    useEffect(() => {
        const lastPage = Math.max(1, pageCount);
        if (currentPage > lastPage) {
            setCurrentPage(lastPage);
        }
    }, [pageCount, currentPage])

    const paginatedReasons = reasons
        .filter(reason => !reason.isDeleted)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    async function updateReasonOrderInBackend(updatedReasons: ResignationReason[]) {
        try {
            setIsUpdating(true);

            const orderedIds = updatedReasons
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((reason) => reason.id);

            updateResignationReasonOrder(token, orderedIds)
                .then((response: ResignationReason[] | void) => {
                    if (response) {
                        setReasons(response)
                    }
                })
                .catch((error) => {
                    log.error("Error updating reasons for backend: ", error);

                    if (error.validationIssues) {
                        setOrderUpdateIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setOrderUpdateIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setOrderUpdateIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }

                })
                .finally(() => {
                    setHasOrderChanged(false);
                    setIsUpdating(false)
                });
        } catch (error) {
            log.error("Error while updating order:", error);

            showToast(
                t("adminSettings.resignationReasons.toast.updateOrderError.title"),
                t("adminSettings.resignationReasons.toast.updateOrderError.description"),
                "destructive"
            )

            setReasons(prevReasons);
            setIsUpdating(false);
        }
    }

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            setPrevReasons(reasons)

            setReasons((items: ResignationReason[]) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);

                const newItems = arrayMove(items, oldIndex, newIndex);

                setHasOrderChanged(true)

                return newItems.map((item, index) => ({
                    ...item,
                    sortOrder: index + 1
                }));
            });
        }
    }

    function handleStartEditing(id: string) {
        setEditingId(id);
    }

    function handleCancelEditing() {
        setEditingId(null);
    }

    function handleSaveEditing(id: string, newName: string) {
        setReasons(reasons.map(reason =>
            reason.id === id
                ? {...reason, name: newName}
                : reason
        ));

        try {
            setIsUpdating(true);

            const dto = {
                id: id,
                name: newName,
            }

            updateResignationReasonDetails(token, dto)
                .then((response: ResignationReason | void) => {
                    log.debug("Success: ", response);

                    showToast(
                        t("adminSettings.resignationReasons.toast.updated.title"),
                        t("adminSettings.resignationReasons.toast.updated.description", {reasonName: newName}),
                    )

                    setEditReasonIssue(null);
                    setEditingId(null);
                    setIsUpdating(false);

                    fetchResignationReasons()
                })
                .catch((error) => {
                    log.error(`Error updating ${id}: `, error);

                    if (error.validationIssues) {
                        setEditReasonIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setEditReasonIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setEditReasonIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }
                })


        } catch (error) {
            log.error("Error while updating resignation reason: ", error);
            showToast(
                t("adminSettings.resignationReasons.toast.editError.title"),
                t("adminSettings.resignationReasons.toast.editError.description"),
                "destructive"
            )
        } finally {
            setIsUpdating(false);
        }

    }

    function handleDelete(id: string) {
        try {
            setIsUpdating(true);
            const response = deleteResignationReason(token, id);

            response
                .then(() => {
                    log.debug(`Successfully deleted ${id}`);

                    showToast(
                        t("adminSettings.resignationReasons.toast.deleted.title"),
                        t("adminSettings.resignationReasons.toast.deleted.description"),
                    )

                    fetchResignationReasons()
                })
                .catch((error) => {
                    log.error("Promise errored: ", error);

                    showToast(
                        t("adminSettings.resignationReasons.toast.deleteError.title"),
                        error.validationIssues
                            ? t(error.validationIssues[0].message)
                            : t("adminSettings.resignationReasons.toast.deleteError.description"),
                        "destructive"
                    )
                    fetchResignationReasons()
                }).finally(() => setIsUpdating(false))

        } catch (error) {
            fetchResignationReasons()
            log.error(`Error deleting entry with id ${id}:`, error);

            showToast(
                t("adminSettings.resignationReasons.toast.deleteError.title"),
                t("adminSettings.resignationReasons.toast.deleteError.description"),
                "destructive"
            )

        }
    }

    function handleAddNewReason() {
        if (newReason.trim() === "") return;

        const newEntry: ResignationReason = {
            id: uuid(),
            name: newReason,
            sortOrder: reasons.length + 1,
        };

        try {
            setIsUpdating(true)

            const response = createResignationReason(token, newEntry);

            response
                .then((created: ResignationReason | void) => {
                    if (created) {
                        setReasons([...reasons, created]);

                        showToast(
                            t("adminSettings.resignationReasons.toast.created.title"),
                            t("adminSettings.resignationReasons.toast.created.description", {
                                reasonName: created.name
                            }),
                        )

                        setNewReason("");
                        setIsAdding(false);
                        setAddingIssue(null)
                    }
                })
                .catch((error) => {
                    if (error.validationIssues) {
                        setAddingIssue(t(error.validationIssues[0].message));
                    } else if (error.status) {
                        setAddingIssue(tCommon("serverError.responseCodeWithDetails", {
                            statusCode: error.status,
                            errorDetails: error.error
                        }));
                    } else {
                        setAddingIssue(tCommon("serverError.unknownWithDetails", {errorDetails: error.message}));
                    }
                }).finally(() => setIsUpdating(false))
        } catch (error) {
            log.error("Unable to create new resignation reason.", error);


            showToast(
                t("adminSettings.resignationReasons.toast.creationError.title"),
                t("adminSettings.resignationReasons.toast.creationError.description", {
                    reasonName: newReason
                }),
                "destructive"
            )

            setIsAdding(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            {isUpdating && (
                <div
                    className="absolute inset-0 bg-white/60 dark:bg-zinc-950/60 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white dark:bg-zinc-900 p-4 rounded-lg shadow-lg flex items-center gap-3">
                        <LoadingSpinner/>
                        <Text>{tCommon("loading")}</Text>
                    </div>
                </div>
            )}

            <CardHeader>
                <div className="flex justify-between items-start gap-4">
                    <div className="max-w-2xl">
                        <CardTitle className="text-xl font-bold flex items-center gap-2">
                            {t("adminSettings.resignationReasons.title")}
                            {isMissing && (
                                <span
                                    title={t("adminSettings.missingConfiguration.tooltip")}
                                    aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                                >
                                    <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                </span>
                            )}
                        </CardTitle>
                        <Text className="mt-1 text-sm text-muted-foreground">{t("adminSettings.resignationReasons.description")}</Text>
                    </div>
                    {!isAdding && (
                        <Button
                            outline
                            onClick={() => setIsAdding(true)}
                            disabled={isUpdating}
                            data-testid="add-resignation-reason-button"
                            className="whitespace-nowrap"
                        >
                            <Plus className="h-5 w-5 mr-2" data-slot="icon"/>
                            {t("adminSettings.resignationReasons.addNew")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            {orderUpdateIssue && (
                <ValidationAlert message={orderUpdateIssue || tCommon("serverErrors.unknown")}/>
            )}

            {editReasonIssue && (
                <ValidationAlert message={editReasonIssue || tCommon("serverErrors.unknown")}/>
            )}

            <CardContent>


                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={paginatedReasons.map(reason => reason.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-3 mb-6">
                            {paginatedReasons.map((reason) => (
                                <SortableItem
                                    key={reason.id}
                                    reason={reason}
                                    editingId={editingId}
                                    handleStartEditing={handleStartEditing}
                                    handleSaveEditing={handleSaveEditing}
                                    handleCancelEditing={handleCancelEditing}
                                    handleDelete={handleDelete}
                                    t={t}
                                    isUpdating={isUpdating}
                                    disableDelete={reasons.length <= 1}
                                />
                            ))}
                            {paginatedReasons.length === 0 && (
                                <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                                    <Text className="text-blue-700 dark:text-blue-300">
                                        {t("adminSettings.resignationReasons.noItems")}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>

                {isAdding && (
                    <>
                        <div className="flex items-center gap-2 mt-4 mb-2">
                            <Input
                                value={newReason}
                                onChange={(e) => setNewReason(e.target.value)}
                                placeholder={t("adminSettings.resignationReasons.namePlaceholder")}
                                className="flex-grow"
                                data-testid="resignation-reason-name"
                            />
                            <Button
                                color="green"
                                onClick={handleAddNewReason}
                                data-testid="submit-resignation-reason"
                            >
                                {t("adminSettings.resignationReasons.saveNew")}
                            </Button>
                            <Button
                                outline
                                onClick={() => {
                                    setNewReason("")
                                    setIsAdding(false)
                                }}
                            >
                                {t("adminSettings.resignationReasons.cancel")}
                            </Button>
                        </div>
                        {addingIssue && (
                            <ValidationAlert message={addingIssue}/>
                        )}
                    </>
                )}

                {pageCount > 1 && (
                    <div className="mt-6 flex justify-center">
                        <Pagination
                            count={pageCount}
                            page={currentPage}
                            onChange={(page) => setCurrentPage(page)}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default SectionResignationReason;
